// Centralized locale configuration for the entire application
// This will fetch languages from the database via API
import logger from '../utils/logger.js';

// ============================================================================
// LANGUAGE CONSTANTS - Shared across client components
// ============================================================================

// Language to ISO 639-1 abbreviation mapping
export const LANGUAGE_ABBREV = {
  'English': 'EN',
  'Italian': 'IT',
  'French': 'FR',
  'German': 'DE',
  'Spanish': 'ES',
  'Chinese': 'ZH',
  'Korean': 'KO',
  'Japanese': 'JA'
};

// BCP 47 language codes for Web Speech API (ordered by priority)
// These are used for speech recognition to find the best matching voice
export const LANGUAGE_CODES = {
  'English': ['en-US', 'en-GB', 'en-AU', 'en'],
  'Italian': ['it-IT', 'it'],
  'French': ['fr-FR', 'fr-CA', 'fr'],
  'German': ['de-DE', 'de-AT', 'de-CH', 'de'],
  'Spanish': ['es-US', 'es-MX', 'es-ES', 'es'],  // US/Mexico prioritized over Spain
  'Chinese': ['zh-TW', 'zh-HK', 'zh-CN', 'zh'],  // Taiwan prioritized
  'Korean': ['ko-KR', 'ko'],
  'Japanese': ['ja-JP', 'ja'],
};

export const LANGUAGE_COLUMNS = [
  { key: 'english', label: 'English' },
  { key: 'italian', label: 'Italian' },
  { key: 'french', label: 'French' },
  { key: 'german', label: 'German' },
  { key: 'spanish', label: 'Spanish' },
  { key: 'chinese', label: 'Chinese' },
  { key: 'korean', label: 'Korean' },
  { key: 'japanese', label: 'Japanese' },
];

export const SUPPORTED_LANGUAGES = LANGUAGE_COLUMNS.map((col) => col.label);

// ============================================================================
// DYNAMIC LOCALES - Fetched from API/Database
// ============================================================================

// Cache for languages data
let languagesCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fallback hardcoded locales in case API is unavailable
const FALLBACK_LOCALES = {
  'en-US': {
    code: 'en-US',
    name: 'English (US)',
    language: 'English',
    flag: '🇺🇸'
  },
  'en-GB': {
    code: 'en-GB', 
    name: 'English (UK)',
    language: 'English',
    flag: '🇬🇧'
  },
  'it-IT': {
    code: 'it-IT',
    name: 'Italiano',
    language: 'Italian', 
    flag: '🇮🇹'
  }
};

// Function to fetch languages from API
async function fetchLanguagesFromAPI() {
  try {
    logger.debug('Fetching languages from API...');
    
    const response = await fetch('/api/admin/languages/public');
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const languagesData = await response.json();
    
    // Convert array to object format
    const languages = {};
    languagesData.forEach(lang => {
      languages[lang.code] = {
        code: lang.code,
        name: lang.name,
        language: lang.language,
        flag: lang.flag
      };
    });
    
    logger.info(`Successfully fetched ${languagesData.length} languages from API`);
    return languages;
  } catch (error) {
    console.error('❌ Failed to fetch languages from API:', error);
    logger.warn('Falling back to hardcoded locales');
    return FALLBACK_LOCALES;
  }
}

// Function to get cached languages or fetch from API
async function getLanguages() {
  const now = Date.now();
  
  // Check if cache is valid
  if (languagesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    return languagesCache;
  }
  
  // Fetch from API and update cache
  languagesCache = await fetchLanguagesFromAPI();
  cacheTimestamp = now;
  
  return languagesCache;
}

// Function to invalidate cache (call this when languages are updated)
function invalidateLanguagesCache() {
  logger.debug('Invalidating languages cache');
  languagesCache = null;
  cacheTimestamp = null;
}

// Default locale
export const DEFAULT_LOCALE = 'en-US';

// Helper function to get locale by code
export const getLocaleByCode = async (code) => {
  const locales = await getLanguages();
  return locales[code] || locales[DEFAULT_LOCALE];
};

// Helper function to get all locale options for dropdowns (client-side)
export const getLocaleOptions = async () => {
  const locales = await getLanguages();
  return Object.values(locales).map(locale => ({
    value: locale.code,
    label: `${locale.flag} ${locale.name}`,
    ...locale
  }));
};

// Helper function to map locale to language (for backend compatibility)
export const mapLocaleToLanguage = async (localeCode) => {
  const locale = await getLocaleByCode(localeCode);
  return locale.language;
};

// Helper function to get all supported locales
export const getSupportedLocales = async () => {
  return await getLanguages();
};

// Synchronous fallback functions for backward compatibility
export const getLocaleByCodeSync = (code) => {
  return FALLBACK_LOCALES[code] || FALLBACK_LOCALES[DEFAULT_LOCALE];
};

export const mapLocaleToLanguageSync = (localeCode) => {
  const locale = getLocaleByCodeSync(localeCode);
  return locale.language;
};

export const getSupportedLocalesSync = () => {
  return FALLBACK_LOCALES;
};

// Synchronous fallback for getLocaleOptions
export const getLocaleOptionsSync = () => {
  return Object.values(FALLBACK_LOCALES).map(locale => ({
    value: locale.code,
    label: `${locale.flag} ${locale.name}`,
    ...locale
  }));
};

// Export cache management functions
export { getLanguages, invalidateLanguagesCache };

// Export FALLBACK_LOCALES for reference
export { FALLBACK_LOCALES };
